import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { PurchaseOrdersService, PurchaseOrder } from '../../services/purchase-orders.service';
import { PurchaseOrderDialogComponent, PurchaseOrderDialogData } from './purchase-order-dialog.component';

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.css']
})
export class PurchaseOrdersComponent implements OnInit {
  displayedColumns: string[] = ['id', 'orderNumber', 'supplierName', 'orderDate', 'expectedDeliveryDate', 'status', 'totalAmount', 'itemCount', 'createdBy', 'actions'];
  dataSource = new MatTableDataSource<PurchaseOrder>();
  isLoading = false;
  selectedDate: Date = new Date();
  totalOrders: number = 0;
  pendingOrders: number = 0;
  completedOrders: number = 0;
  totalValue: number = 0;

  constructor(
    private purchaseOrdersService: PurchaseOrdersService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadPurchaseOrders();
  }

  loadPurchaseOrders(): void {
    this.isLoading = true;
    this.purchaseOrdersService.getPurchaseOrders().subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource.data = response.data;
          this.calculateTotals();
        } else {
          this.snackBar.open(response.message || 'Failed to load purchase orders', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading purchase orders:', error);
        this.snackBar.open('Error loading purchase orders', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  calculateTotals(): void {
    this.totalOrders = this.dataSource.data.length;
    this.pendingOrders = this.dataSource.data.filter(item => item.status === 'Pending').length;
    this.completedOrders = this.dataSource.data.filter(item => item.status === 'Delivered').length;
    this.totalValue = this.dataSource.data.reduce((sum, item) => sum + item.totalAmount, 0);
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  dateChange(event: MatDatepickerInputEvent<Date>): void {
    this.selectedDate = event.value || new Date();
    this.loadPurchaseOrders(); // Reload with new date filter
  }

  addPurchaseOrder(): void {
    const dialogData: PurchaseOrderDialogData = {
      title: 'Add New Purchase Order'
    };

    const dialogRef = this.dialog.open(PurchaseOrderDialogComponent, {
      width: '1000px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadPurchaseOrders(); // Refresh the list
      }
    });
  }

  editOrder(order: PurchaseOrder): void {
    const dialogData: PurchaseOrderDialogData = {
      title: 'Edit Purchase Order',
      purchaseOrder: order
    };

    const dialogRef = this.dialog.open(PurchaseOrderDialogComponent, {
      width: '1000px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadPurchaseOrders(); // Refresh the list
      }
    });
  }

  viewOrderDetails(order: PurchaseOrder): void {
    this.snackBar.open(`Viewing details for ${order.orderNumber}`, 'Close', { duration: 3000 });
    // TODO: Implement order details dialog
  }

  approveOrder(order: PurchaseOrder): void {
    this.snackBar.open(`Approving order ${order.orderNumber}...`, 'Close', { duration: 3000 });
    // TODO: Implement order approval
  }

  cancelOrder(order: PurchaseOrder): void {
    this.snackBar.open(`Cancelling order ${order.orderNumber}...`, 'Close', { duration: 3000 });
    // TODO: Implement order cancellation with confirmation
  }

  exportToExcel(): void {
    this.snackBar.open('Exporting to Excel...', 'Close', { duration: 3000 });
    this.purchaseOrdersService.exportToExcel().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `purchase-orders-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Excel export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
        this.snackBar.open('Error exporting to Excel', 'Close', { duration: 3000 });
      }
    });
  }

  exportToPDF(): void {
    this.snackBar.open('Exporting to PDF...', 'Close', { duration: 3000 });
    this.purchaseOrdersService.exportToPDF().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `purchase-orders-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('PDF export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to PDF:', error);
        this.snackBar.open('Error exporting to PDF', 'Close', { duration: 3000 });
      }
    });
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Pending': return 'badge-warning';
      case 'Processing': return 'badge-info';
      case 'Delivered': return 'badge-success';
      case 'Cancelled': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }
}
